import { StrictMode, Suspense, lazy, useEffect, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "./firebase";
import { SessionProvider, useSession } from "./session";
import { ROLE_LABEL, canReview, canSeeAudit, type Member } from "./domain";
import Login from "./pages/Login";
import Overview from "./pages/Overview";
import Documents from "./pages/Documents";
import Upload from "./pages/Upload";
import Placeholder from "./pages/Placeholder";
import Review, { reviewQuery } from "./pages/Review";
import Admin from "./pages/Admin";
import Compliance from "./pages/Compliance";
import Trash from "./pages/Trash";
import Share from "./pages/Share";
import Notifications, { useUnread } from "./pages/Notifications";
// Trình xem PDF nặng (pdf.js) → chỉ tải khi mở tài liệu
const Viewer = lazy(() => import("./pages/Viewer"));
import Birthday from "./Birthday";
import "./styles.css";

/** Số tài liệu đang chờ người này duyệt (hiện trên menu). */
function usePendingCount(m: Member | null) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!m || !canReview(m)) return;
    return onSnapshot(reviewQuery(m), (s) => setN(s.size), () => setN(0));
  }, [m]);
  return n;
}

/** Trang công khai cho người nhận link chia sẻ (không cần đăng nhập). */
function SharedPage() {
  const token = useLocation().pathname.split("/")[2] ?? "";
  return <main className="sharedMain"><Suspense fallback={<p className="muted">Đang mở…</p>}><Viewer share={token} /></Suspense></main>;
}

function Shell() {
  const { user, member, loading, error } = useSession();
  const isShare = useLocation().pathname.startsWith("/s/");
  const pending = usePendingCount(member);
  const unread = useUnread(member?.email);
  // Điện thoại: menu là ngăn kéo trượt từ trái, tự đóng khi chuyển trang
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  if (isShare) return <SharedPage />;
  if (loading) return <div className="login">Đang tải…</div>;
  if (!user) return <Login />;
  if (!member) return (
    <div className="login"><div className="card"><div className="err">{error}</div>
      <button className="btn" onClick={() => signOut(auth)}>Đăng xuất</button></div></div>
  );
  return (
    <div className="app">
      <Birthday email={member.email} />
      <header className="topbar">
        <button className="menuBtn" aria-label="Mở menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
          <span /><span /><span />{pending + unread > 0 && <i className="dot" aria-hidden="true" />}
        </button>
        <img className="mark sm" src="/brand/ahg-mark.png" alt="" />
        <b>AHG Hub</b>
      </header>
      {menuOpen && <div className="backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}
      <aside className={`side${menuOpen ? " open" : ""}`}>
        <div className="brand"><img className="mark" src="/brand/ahg-mark.png" alt="" /><div>AHG Hub<small>An Hưng Group</small></div></div>
        <nav className="nav" aria-label="Điều hướng chính">
          <NavLink to="/" end>Tổng quan</NavLink>
          <NavLink to="/documents" end>Kho tài liệu</NavLink>
          {canReview(member) && <NavLink to="/documents/review">Chờ duyệt{pending > 0 && <span className="badge">{pending}</span>}</NavLink>}
          <NavLink to="/thong-bao">Thông báo{unread > 0 && <span className="badge">{unread}</span>}</NavLink>
          {member.role !== "marketing" && <NavLink to="/compliance">Lịch pháp lý</NavLink>}
          <NavLink to="/assets">Tài sản</NavLink>
          <NavLink to="/stocktake">Kiểm kê</NavLink>
          <NavLink to="/reports">Báo cáo</NavLink>
          {canSeeAudit(member) && <NavLink to="/admin">Phân quyền &amp; nhật ký</NavLink>}
        </nav>
        <div className="who">{member.name}<br />{ROLE_LABEL[member.role]}<br />
          <button onClick={() => signOut(auth)}>Đăng xuất</button></div>
      </aside>
      <main>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/documents/upload" element={<Upload />} />
          <Route path="/documents/trash" element={<Trash />} />
          <Route path="/documents/:docId/share" element={<Share />} />
          <Route path="/xem/:docId" element={<Suspense fallback={<p className="muted">Đang mở trình xem…</p>}><Viewer /></Suspense>} />
          <Route path="/documents/review" element={canReview(member) ? <Review /> : <Navigate to="/documents" />} />
          <Route path="/compliance" element={member.role !== "marketing" ? <Compliance /> : <Navigate to="/" />} />
          <Route path="/thong-bao" element={<Notifications />} />
          <Route path="/assets" element={<Placeholder title="Sổ tài sản" phase="GĐ2 · AST-01 → AST-07" />} />
          <Route path="/stocktake" element={<Placeholder title="Kiểm kê" phase="GĐ2 · STK-01 → STK-04" />} />
          <Route path="/reports" element={<Placeholder title="Báo cáo ban giám đốc" phase="GĐ3 · RPT-02" />} />
          <Route path="/admin" element={canSeeAudit(member) ? <Admin /> : <Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => undefined);

createRoot(document.getElementById("root")!).render(
  <StrictMode><SessionProvider><BrowserRouter><Shell /></BrowserRouter></SessionProvider></StrictMode>,
);
